import { useCallback, memo, Fragment } from 'react';
import { clipboardWrite } from '@quicktools/shared-components/utils/text/clipboard';
import { FontCategory } from '@quicktools/shared-components/data/fonts';
import useSnackbarStore from '@quicktools/shared-components/hooks/snackbar';
import { useTranslation } from 'react-i18next';
import { GoogleAd, useShowAd } from '@quicktools/shared-components/components/GoogleAd';
import {
  GOOGLE_AD_MANAGER_QT_LEADER_BOARD_MOBILE_2,
  GOOGLE_AD_MANAGER_QT_RIGHT_RAIL_MOBILE_2,
} from '@quicktools/shared-components/services/constants';

import { useStore } from '../../store';
import LoadMore from '../common/LoadMore';
import { FontRow, AsciiFontRow } from '../FontRow';
import analytics from '../../services/analytics';
import { defaultText } from '../../constants';
import storage from '../../utils/common/localStorage';

import { GeneratorCopyData, GeneratorProps } from './types';
import useStyles from './styles';

const LOAD_COUNT = 15;

function Generator({ fonts, setRecentsCategoryVisible, displayCategoryName }: GeneratorProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const rawText = useStore((state) => state.mainText || defaultText);
  const addToEditor = useStore((state) => state.addToEditor);
  const setEditorExpanded = useStore((state) => state.setEditorExpanded);
  const currentSelectedFont = useStore((state) => state.currentSelectedFont);
  const setCurrentSelectedFont = useStore((state) => state.setCurrentSelectedFont);
  const copiedFont = useStore((state) => state.copiedFont);
  const setCopiedFont = useStore((state) => state.setCopiedFont);
  const snackbarShowWarning = useSnackbarStore((state) => state.snackbarShowWarning);
  const { showDesktopAd, showMobileAd } = useShowAd();

  const copyText = useCallback(async ({ font, fontifiedText }: GeneratorCopyData) => {
    let copied = true;
    try {
      await clipboardWrite(fontifiedText);
      setCopiedFont(font);
    } catch (err) {
      copied = false;
      snackbarShowWarning(t('error.clipboardCopy'));
      console.error(err);
    }

    analytics.fontCopy('copy_icon', font.name, font.id + 1);
    addToEditor(fontifiedText, font);
    setCurrentSelectedFont(font);
    setEditorExpanded(true);
    setRecentsCategoryVisible(true);
    storage.addNewEntry(font.id);

    return copied;
  }, []);

  return (
    <div className={styles.root}>
      <LoadMore loadCount={LOAD_COUNT} hideSeeAllButton={displayCategoryName}>
        {fonts.map((font, index) => {
          const props = {
            index,
            font,
            copyText,
            rawText,
            isActive: font === currentSelectedFont,
            isCopied: font === copiedFont,
            displayCategoryName,
          };

          return (
            <Fragment key={`gen-${index}`}>
              {index === 9 && (
                <>
                  {showMobileAd && (
                    <>
                      <GoogleAd
                        className={styles.centered}
                        id={GOOGLE_AD_MANAGER_QT_LEADER_BOARD_MOBILE_2}
                      />
                      <GoogleAd
                        className={styles.centered}
                        id={GOOGLE_AD_MANAGER_QT_RIGHT_RAIL_MOBILE_2}
                      />
                    </>
                  )}
                </>
              )}
              {font.mainCategory === FontCategory.TextArt ? (
                <AsciiFontRow {...props} />
              ) : (
                <FontRow {...props} />
              )}
            </Fragment>
          );
        })}
      </LoadMore>
    </div>
  );
}

export default memo(Generator);
